import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Series: Hormones & Mood — "The Doctor Who Couldn't Figure Out Her Own Hormones".

export const perimenopauseRuth: Scenario = {
  slug: 'perimenopause-ruth',
  series: 'Hormones & Mood',
  title: "Her HRT looked right on paper. Her body disagreed.",
  socialHook:
    "Ruth, 49, is a family doctor. She started HRT early, used it carefully, and still woke at 3am with brain fog and mood swings. Her labs looked reassuring. Her genetics explained why the number on paper was not the effect in her body.",
  meta: {
    description:
      "A fictional case study: Ruth is a family doctor whose perimenopause symptoms persisted despite HRT. Her CYP1B1, COMT, and SHBG genetics explain why total estradiol can look adequate while active estrogen exposure falls short.",
  },

  persona: {
    name: 'Ruth',
    age: 49,
    sex: 'Female',
    ancestry: 'White European',
    occupation: 'Family doctor who co-owns a practice',
    chiefConcern:
      'Perimenopause symptoms starting at 46 — earlier than she expected. Hot flashes, sleep disruption, brain fog, joint aches, and mood swings. Started HRT at 48 and has had her dose increased twice. Still symptomatic.',
    familyHistory:
      'Mother: menopause at 44 (early). Sister: also on HRT, also struggling to achieve symptom control.',
  },

  clinicalPicture: {
    symptoms: [
      'Hot flashes — persistent despite HRT, worse in the evenings',
      'Sleep disruption — waking at 3am, unable to return to sleep',
      'Brain fog — cognitive slowing noticeable in clinic; she describes it as "thinking through cotton wool"',
      'Mood swings and low mood — out of character; she finds this the most distressing symptom',
    ],
    labs: [
      {
        name: 'Estradiol (on HRT)',
        value: '57 pg/mL',
        refRange: '54–109 pg/mL (HRT therapeutic range)',
        flag: 'normal',
      },
      {
        name: 'SHBG',
        value: '98 nmol/L',
        refRange: '20–80 nmol/L',
        flag: 'high',
      },
    ],
    medications: [
      'Estradiol transdermal patch (dose increased twice)',
      'Oral progesterone (micronized — recently switched from synthetic)',
    ],
    supplements: [
      'Vitamin D 1000 IU/day (self-prescribed)',
      'Magnesium glycinate 400mg at night',
    ],
    lifestyle: [
      'Active lifestyle — cycles to work, runs 2–3×/week',
      'Non-smoker, alcohol 4–5 drinks/week (glass of wine most evenings)',
      'Diet: generally good; low in processed foods',
      'Sleep: historically good; severely disrupted since perimenopause onset',
    ],
  },

  genetics: [
    {
      gene: 'CYP1B1',
      variant: 'Overactive variant',
      rsid: 'rs1056836',
      genotype: 'Higher-activity allele',
      plainMeaning:
      "The patch may be delivering estrogen, but Ruth's body may be moving it out of the useful range too quickly. Her result points to faster conversion into weaker estrogen forms before enough effect reaches her tissues.",
      technicalDetail:
        "CYP1B1 metabolizes estrogen into catechol estrogen forms — specifically the 4-OH catechol estrogen metabolite, which is less biologically active than estradiol itself. Ruth's higher-activity variant accelerates this conversion, meaning the estradiol delivered by her patch is being processed into less active forms faster than average. Blood levels that look adequate on a test may not be providing the cellular effect those levels imply.",
    },
    {
      gene: 'COMT',
      variant: 'Val/Val',
      rsid: 'rs4680',
      genotype: 'Val/Val (fast metabolizer)',
      plainMeaning:
      "Once estrogen is converted into those weaker forms, Ruth may clear them quickly too. That leaves less time for the hormone signal to build to a level her symptoms can feel.",
      technicalDetail:
        "COMT (catechol-O-methyltransferase) clears the catechol estrogen metabolites that CYP1B1 produces. The Val/Val variant is the fast-clearing version. Combined with overactive CYP1B1, this creates a two-stage acceleration: estradiol is rapidly converted into less active catechol forms, and those metabolites are then rapidly cleared. Her effective estrogen exposure is lower than her blood test number suggests.",
    },
    {
      gene: 'SHBG',
      variant: 'High-SHBG variant',
      rsid: 'rs6259',
      genotype: 'Higher-SHBG allele',
      plainMeaning:
      "Her lab number includes estrogen that is locked onto a carrier protein and cannot be used. So the total estradiol result can look fine while the active amount is still too low.",
      technicalDetail:
        "Sex hormone-binding globulin (SHBG) is a carrier protein that binds estrogen in the bloodstream. Only unbound (free) estrogen is biologically active. Ruth's variant predisposes her to higher SHBG levels, and oral progesterone independently raises SHBG further. The standard estradiol test measures total estradiol — including the bound, inactive fraction. Her free (active) estradiol is substantially lower than the total number implies.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "She knew the treatment. It still was not working.",
      body: "Ruth is a family doctor who co-owns a practice. She knows the menopause guidelines and recognized her own perimenopause early. She started HRT at 48, used it carefully, and increased the dose twice. Her patients often improve on the same approach. She did not. She was still waking at 3am, losing her train of thought in exam rooms, and feeling mood swings that were completely unlike her. Knowing the theory made the mismatch more frustrating, not less.",
    },
    {
      type: 'narrative',
      heading: 'A normal number can still miss the effect',
      body: "Ruth's estradiol result sits inside the usual HRT range. On paper, the dose looks adequate. But that number hides three steps between the patch and her cells: how fast estrogen is converted into weaker forms, how fast those forms are cleared, and how much measured estradiol is free rather than locked onto a carrier protein. The blood test measures the pool. It does not automatically show how much hormone effect is reaching the target tissue.",
    },
    {
      type: 'reveal',
      heading: "Her estrogen may be leaving too quickly",
      body: "Ruth is not failing HRT. Her body may be processing it faster than the standard dose assumes. CYP1B1 can convert estradiol from the patch into weaker catechol estrogen forms, and her COMT result can clear those forms quickly. Together, those steps can shorten the useful hormone signal. The clue is that her symptoms persisted despite careful use, not because she used the treatment incorrectly.",
    },
    {
      type: 'reveal',
      heading: "The active estrogen may be lower than the total number",
      body: "Ruth's SHBG is high. That matters because SHBG binds estradiol in the bloodstream and keeps it inactive. Only unbound estradiol can enter cells. At her SHBG level, a large share of the total estradiol result may be unavailable, especially with oral progesterone adding pressure in the same direction. The number on the report is real, but it may not be the number her tissues are feeling.",
    },
    {
      type: 'action',
      heading: "Five conversations worth having with a menopause specialist",
      items: [
        "Ask the specialist to calculate free estradiol rather than relying on total estradiol alone. The calculation uses total estradiol and SHBG together; at Ruth's SHBG level, the gap between total and free may explain why symptoms persist.",
        "Discuss whether oral progesterone is the best fit. Oral progesterone is absorbed systemically and can raise SHBG, which may compound the estrogen availability problem. A Mirena IUD delivers progesterone locally to the endometrium with minimal systemic absorption and does not raise SHBG.",
        "Share the CYP1B1 and COMT context with the specialist. Evidence for personalizing HRT dose based on metabolic genotype is still emerging, but it gives a plausible explanation for why standard doses have fallen short and may support a carefully monitored dose discussion.",
        "Ask about DIM (diindolylmethane), a compound found in cruciferous vegetables and available as a supplement. DIM may shift CYP1B1 estrogen metabolism toward a less reactive pathway. It is an adjunct, not a substitute for HRT review, but it matches the metabolic pattern.",
        "Recheck vitamin D with a target of at least 30 ng/mL. Ruth is supplementing but still below the functional threshold — she may have a variant that limits absorption. A higher dose and a retest in two months would clarify whether she's a low responder.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'streak' as const,
      heading: "Ruth's 8-week HRT timing and DIM goal",
      goalLabel: 'Consistent HRT timing + DIM supplementation',
      caption:
        "Ruth set a goal to apply her patch at the same time each day and track DIM supplementation. More consistent timing reduced day-to-day swings and gave her specialist a clearer picture of what still needed adjusting.",
      weeks: [
        {
          weekLabel: 'Week 1',
          days: [true, false, true, false, true, false, false],
          note: "Patch timing all over the place — evening shifts and a weekend away disrupted the routine. Three out of seven days.",
        },
        {
          weekLabel: 'Week 2',
          days: [true, true, false, true, false, true, false],
          note: "Better structure mid-week. Still missing weekend doses. Starting DIM supplementation.",
        },
        {
          weekLabel: 'Week 3',
          days: [true, true, true, false, true, true, false],
          note: "Five days — first week I've felt like I'm actually building a habit. Hot flashes slightly less severe.",
        },
        {
          weekLabel: 'Week 4',
          days: [true, true, false, true, true, true, true],
          note: "Five days again. Missed Wednesday but recovered. Sleep still disrupted but slightly better.",
        },
        {
          weekLabel: 'Week 5',
          days: [true, true, true, true, true, false, true],
          note: "Six days. First time I've done a weekend dose consistently. Brain fog noticeably improved at work.",
        },
        {
          weekLabel: 'Week 6',
          days: [true, true, true, true, true, true, false],
          note: "Six days. Waking at 3am only twice this week — down from most nights. Mood more stable.",
        },
        {
          weekLabel: 'Week 7',
          days: [true, true, true, true, true, true, true],
          note: "First perfect week. Patch timing has become automatic. Joint stiffness in the morning noticeably reduced.",
        },
        {
          weekLabel: 'Week 8',
          days: [true, true, true, true, true, true, false],
          note: "Six days. Specialist appointment booked — going in with the free estradiol calculation done. Feel like I'm advocating for myself properly for the first time.",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'perimenopause-ruth',
    label: 'Explore your hormone genetics in Coach',
  },
}
